import React, { useEffect, useMemo, useState } from "react";
import { Button, Input } from "reactstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import type { AccionReceta, AccionRecetaSwitch, RecetaResumen } from "../Recetas";
import {
  buildVelzonDataTableStyles,
  ensureVelzonDataTableThemes,
} from "../../../../Components/Common/dataTableTheme";

ensureVelzonDataTableThemes();

type RecetasListadoTablaProps = {
  recetas: RecetaResumen[];
  accionesReceta: AccionReceta[];
  esSwitch: (accion: AccionReceta) => accion is AccionRecetaSwitch;
  mostrarAccionPendiente: (accionLabel: string, receta: RecetaResumen) => void;
  onEditarReceta: (receta: RecetaResumen) => void;
  onVerReceta: (receta: RecetaResumen) => void;
  onVerHistorial: (receta: RecetaResumen) => void;
  onToggleActivo: (receta: RecetaResumen) => void;
  onAnularReceta: (receta: RecetaResumen) => void;
  accionProcesando: { id: number; key: AccionRecetaSwitch["key"] } | null;
  estadoBadgeColor: Record<string, string>;
  formatearFechaUsuario: (fecha?: string | null, nombre?: string | null) => string;
};

const formatFechaCorta = (valor?: string | null) => {
  if (!valor) return null;
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) {
    return null;
  }
  return fecha.toLocaleDateString();
};

const RecetasListadoTabla: React.FC<RecetasListadoTablaProps> = ({
  recetas,
  accionesReceta,
  esSwitch,
  mostrarAccionPendiente,
  onEditarReceta,
  onVerReceta,
  onVerHistorial,
  onToggleActivo,
  onAnularReceta,
  accionProcesando,
  estadoBadgeColor,
  formatearFechaUsuario,
}) => {
  const accionInactivar = accionesReceta.find(
    (accion): accion is AccionRecetaSwitch => esSwitch(accion) && accion.key === "inactivar",
  );
  const accionAnular = accionesReceta.find(
    (accion): accion is AccionRecetaSwitch => esSwitch(accion) && accion.key === "anular",
  );
  const accionesBoton = accionesReceta.filter((accion) => !esSwitch(accion));

  const getThemeFromDocument = () => {
    if (typeof document === "undefined") {
      return "light" as const;
    }
    const attr =
      document.documentElement?.getAttribute("data-bs-theme") ??
      document.body?.getAttribute("data-bs-theme");
    return attr === "dark" ? "dark" : "light";
  };

  const [themeMode, setThemeMode] = useState<"light" | "dark">(getThemeFromDocument);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    const updateMode = () => {
      setThemeMode(getThemeFromDocument());
    };
    updateMode();
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.attributeName === "data-bs-theme")) {
        updateMode();
      }
    });
    const targets = [document.documentElement, document.body].filter(Boolean) as Element[];
    targets.forEach((target) => observer.observe(target, { attributes: true, attributeFilter: ["data-bs-theme"] }));
    return () => observer.disconnect();
  }, []);

  const dataTableStyles = useMemo(() => {
    const base = buildVelzonDataTableStyles(themeMode);
    const cardBackground = "var(--bs-card-bg, var(--bs-body-bg))";
    const headBackground = cardBackground;
    return {
      ...base,
      table: {
        ...base.table,
        style: {
          ...base.table?.style,
          fontSize: "0.9rem",
          backgroundColor: cardBackground,
          border: "1px solid var(--bs-border-color)",
          borderRadius: "0.5rem",
          overflow: "hidden",
        },
      },
      rows: {
        ...base.rows,
        style: {
          ...base.rows?.style,
          fontSize: "0.9rem",
          backgroundColor: cardBackground,
          borderBottom: "1px solid var(--bs-border-color)",
        },
      },
      cells: {
        style: {
          borderRight: "1px solid var(--bs-border-color)",
        },
      },
      headCells: {
        ...base.headCells,
        style: {
          ...base.headCells?.style,
          fontSize: "0.78rem",
          paddingTop: "0.85rem",
          paddingBottom: "0.85rem",
          backgroundColor: headBackground,
          color: "var(--bs-body-color)",
          borderBottomColor: "var(--bs-border-color)",
        },
      },
      headRow: {
        ...base.headRow,
        style: {
          ...(base.headRow?.style || {}),
          backgroundColor: headBackground,
          borderBottom: "1px solid var(--bs-border-color)",
        },
      },
      pagination: {
        ...base.pagination,
        style: {
          ...base.pagination?.style,
          backgroundColor: cardBackground,
          borderTopColor: "var(--bs-border-color)",
        },
      },
    };
  }, [themeMode]);

  const columns = useMemo<TableColumn<RecetaResumen>[]>(() => {
    const renderVigenciaValor = (receta: RecetaResumen, valor?: string | null) => {
      const estadoCambioLabel = receta.estado === "anulado" ? "Anulada" : receta.estado === "inactivo" ? "Inactiva" : null;
      const estadoCambioColor = receta.estado === "anulado" ? "danger" : "warning";
      const estadoCambioFecha = estadoCambioLabel ? formatFechaCorta(receta.actualizadoEn) : null;
      if (!estadoCambioLabel) {
        return valor ?? "-";
      }
      return (
        <div className="d-flex flex-column">
          <span className="text-decoration-line-through text-muted">{valor ?? "-"}</span>
          {estadoCambioFecha && (
            <small className={`text-${estadoCambioColor}`}>
              {estadoCambioLabel} desde {estadoCambioFecha}
            </small>
          )}
        </div>
      );
    };
    const estaProcesando = (receta: RecetaResumen, key: AccionRecetaSwitch["key"]) =>
      Boolean(
        accionProcesando &&
          accionProcesando.id === receta.id &&
          accionProcesando.key === key,
      );
    const renderBotones = (receta: RecetaResumen) => (
      <div className="d-flex flex-wrap gap-2 align-items-center">
        {accionesBoton.map((accion) => {
          const classes =
            "flatClass" in accion && accion.flatClass
              ? `${accion.flatClass} text-white`
              : "softClass" in accion && accion.softClass
              ? accion.softClass
              : `btn-soft-${"color" in accion ? accion.color : "secondary"}`;
          const manejarClick =
            accion.key === "editar"
              ? () => onEditarReceta(receta)
              : accion.key === "ver"
              ? () => onVerReceta(receta)
              : accion.key === "historial"
              ? () => onVerHistorial(receta)
              : () => mostrarAccionPendiente(accion.label, receta);
          const disabled =
            receta.estado !== "activo" && accion.key !== "historial" && accion.key !== "ver";
          return (
            <Button
              key={accion.key}
              size="sm"
              className={classes}
              onClick={manejarClick}
              title={
                disabled
                  ? "Solo se pueden ejecutar sobre recetas activas."
                  : accion.label
              }
              aria-label={accion.label}
              disabled={disabled}
            >
              <i className={`${accion.icon} align-middle me-1`} aria-hidden="true" />
              {accion.label}
            </Button>
          );
        })}
      </div>
    );
    const groupedHeader = (titulo: string) => (
      <span className="text-uppercase text-muted small fw-semibold">{titulo}</span>
    );
    const estadoHeader = (subtitulo: string) => (
      <div className="text-center">
        <div className="text-uppercase text-muted small fw-semibold">Estados operativos</div>
        <div className="fw-semibold">{subtitulo}</div>
      </div>
    );
    return [
      {
        name: groupedHeader("Receta"),
        minWidth: "220px",
        grow: 2,
        sortable: true,
        sortFunction: (rowA, rowB) => rowA.codigo.localeCompare(rowB.codigo),
        cell: (receta) => (
          <div className="d-flex flex-column text-break gap-1">
            <div>
              <span className="text-muted small me-2">ID</span>
              <span className="fw-semibold text-body">{String(receta.id ?? "-")}</span>
            </div>
            <div>
              <span className="text-muted small me-2">Nombre</span>
              <span>{receta.nombre?.trim() || "-"}</span>
            </div>
            <div>
              <span className="text-muted small me-2">Código</span>
              <span>{receta.codigo?.trim() || "-"}</span>
            </div>
          </div>
        ),
      },
      {
        name: groupedHeader("Vigencia"),
        sortable: true,
        selector: (receta) => `${receta.vigenteDesde ?? ""}${receta.vigenteHasta ?? ""}`,
        cell: (receta) => (
          <div className="d-flex flex-column text-break gap-1">
            <div>
              <span className="text-muted small">Desde</span>
              <div>{renderVigenciaValor(receta, receta.vigenteDesde)}</div>
            </div>
            <div>
              <span className="text-muted small">Hasta</span>
              <div>{renderVigenciaValor(receta, receta.vigenteHasta)}</div>
            </div>
          </div>
        ),
        minWidth: "160px",
      },
      {
        name: groupedHeader("Auditoría"),
        cell: (receta) => (
          <div className="d-flex flex-column text-break gap-1">
            <div>
              <span className="text-muted small">Creado</span>
              <div>{formatearFechaUsuario(receta.creadoEn, receta.creadoPorNombre)}</div>
            </div>
            <div>
              <span className="text-muted small">Actualizado</span>
              <div>{formatearFechaUsuario(receta.actualizadoEn, receta.actualizadoPorNombre)}</div>
            </div>
          </div>
        ),
        minWidth: "200px",
      },
      {
        name: groupedHeader("Estado"),
        cell: (receta) => {
          const badgeColor = estadoBadgeColor[receta.estado] ?? "secondary";
          return (
            <span className={`badge bg-${badgeColor}-subtle text-${badgeColor} fw-semibold px-3 py-2`}>
              {receta.estado}
            </span>
          );
        },
        minWidth: "80px",
      },
      {
        name: estadoHeader("Activo"),
        cell: (receta) => (
          <Input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`switch-activo-${receta.id}`}
            checked={receta.estado === "activo"}
            readOnly
            disabled
          />
        ),
        center: true,
        width: "80px",
        ignoreRowClick: true,
        allowOverflow: true,
      },
      {
        name: estadoHeader("Inactivo"),
        cell: (receta) =>
          accionInactivar ? (
            <Input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id={`switch-${accionInactivar.key}-${receta.id}`}
              checked={receta.estado === "inactivo"}
              disabled={receta.estado === "anulado" || estaProcesando(receta, accionInactivar.key)}
              onChange={() => onToggleActivo(receta)}
              title={
                receta.estado === "inactivo"
                  ? "La receta está inactiva. Activala para volver a habilitarla."
                  : "Inactivá la receta para deshabilitarla temporalmente."
              }
            />
          ) : (
            <span className="text-muted">—</span>
          ),
        center: true,
        width: "90px",
        ignoreRowClick: true,
        allowOverflow: true,
      },
      {
        name: estadoHeader("Anulado"),
        cell: (receta) =>
          accionAnular ? (
            <Input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id={`switch-${accionAnular.key}-${receta.id}`}
              checked={receta.estado === "anulado"}
              disabled={receta.estado !== "inactivo" || estaProcesando(receta, accionAnular.key)}
              onChange={() => onAnularReceta(receta)}
              title={
                receta.estado === "anulado"
                  ? "La receta está anulada. No se pueden realizar acciones."
                  : "Anulá la receta (solo disponible cuando está inactiva)."
              }
            />
          ) : (
            <span className="text-muted">—</span>
          ),
        center: true,
        width: "90px",
        ignoreRowClick: true,
        allowOverflow: true,
      },
      {
        name: "Acciones",
        cell: renderBotones,
        grow: 2,
        allowOverflow: true,
        ignoreRowClick: true,
        minWidth: "260px",
      },
    ];
  }, [
    accionAnular,
    accionInactivar,
    accionProcesando,
    accionesBoton,
    estadoBadgeColor,
    formatearFechaUsuario,
    mostrarAccionPendiente,
    onAnularReceta,
    onEditarReceta,
    onToggleActivo,
    onVerHistorial,
    onVerReceta,
  ]);

  return (
    <div className="table-responsive recetas-data-table">
      <DataTable
        columns={columns}
        data={recetas}
        keyField="id"
        pagination
        paginationPerPage={25}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        fixedHeader={false}
        dense
        striped
        responsive
        persistTableHead
        theme={themeMode === "dark" ? "velzonDark" : "velzonLight"}
        customStyles={dataTableStyles}
        noDataComponent={<div className="py-4 text-muted">No hay recetas disponibles.</div>}
      />
      <style>{`
        .recetas-data-table .rdt_TableRow,
        .recetas-data-table .rdt_TableHeadRow {
          border-bottom: 1px solid var(--bs-border-color);
        }
        .recetas-data-table .rdt_TableCell,
        .recetas-data-table .rdt_TableCol {
          border-right: 1px solid var(--bs-border-color);
          border-top: 1px solid var(--bs-border-color);
        }
        .recetas-data-table .rdt_TableCell:last-child,
        .recetas-data-table .rdt_TableCol:last-child {
          border-right: none;
        }
      `}</style>
    </div>
  );
};

export default RecetasListadoTabla;
